import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import useModules from "../hooks/useModules";
import { api, API_BASE, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download } from "lucide-react";
import { toast } from "sonner";
import DateInput from "../components/DateInput";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthStartIso = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Finance() {
    const { modules } = useModules();
    const [from, setFrom] = useState(monthStartIso());
    const [to, setTo] = useState(todayIso());
    const [moduleFilter, setModuleFilter] = useState("__all__");
    const [summary, setSummary] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const ledgerUrl = moduleFilter === "__all__"
            ? `/finance/ledger?date_from=${from}&date_to=${to}`
            : `/finance/ledger?date_from=${from}&date_to=${to}&module_id=${moduleFilter}`;
        Promise.all([
            api.get(`/finance/summary?date_from=${from}&date_to=${to}`),
            api.get(ledgerUrl),
            api.get(`/finance/management-fees?date_from=${from}&date_to=${to}`),
        ])
            .then(([s, l, f]) => { setSummary(s.data); setLedger(l.data.items || []); setFees(f.data.items || []); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [from, to, moduleFilter]);

    if (loading || !summary) return <AppShell title="Finance"><div className="asl-card p-6 text-sm">Loading…</div></AppShell>;

    const grand = summary.grand_totals || {};
    const pipeline = summary.funding_pipeline || {};
    const maxBar = Math.max(1, ...(summary.per_module || []).map((m) => m.gross_total));

    return (
        <AppShell title="Common Finance">
            <section className="space-y-5" data-testid="finance-page">
                <div className="asl-card p-4 flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase">From</div>
                        <DateInput value={from} onChange={setFrom} className="w-40" data-testid="fin-from" />
                    </div>
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase">To</div>
                        <DateInput value={to} onChange={setTo} className="w-40" data-testid="fin-to" />
                    </div>
                    <div className="ml-auto">
                        <a href={`${API_BASE}/finance/ledger/export.csv?date_from=${from}&date_to=${to}`} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" data-testid="fin-csv"><Download className="h-4 w-4 mr-1" /> Ledger CSV</Button>
                        </a>
                    </div>
                </div>

                {/* Per-module cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="fin-modules">
                    {(summary.per_module || []).map((m) => (
                        <div key={m.module_id} className="asl-card p-4 space-y-2" data-testid={`fin-mod-${m.module_code}`}>
                            <div className="flex items-baseline justify-between">
                                <h3 className="text-lg font-bold asl-navy-text">{m.module_name}</h3>
                                <span className="text-xs asl-crimson-text">{m.module_code}</span>
                            </div>
                            <div className="text-xs text-[color:var(--asl-muted)]">{m.worker_count} workers · {m.period_count} periods</div>
                            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                                <Fact label="Gross" value={fmtUSD(m.gross_total)} />
                                <Fact label="Net" value={fmtUSD(m.net_payable_total)} accent />
                                <Fact label="Mgmt fee" value={fmtUSD(m.management_fee)} />
                                <Fact label="Funding req" value={fmtUSD(m.funding_required_total)} />
                                <Fact label="Funded" value={fmtUSD(m.funded_amount)} />
                                <Fact label="Outstanding" value={fmtUSD(m.outstanding_amount)} />
                                {/* Iter63 — Schedule bonus roll-up per module. */}
                                {(m.schedule_bonus_total || 0) > 0 && (
                                    <Fact
                                        label="Schedule bonuses"
                                        value={fmtUSD(m.schedule_bonus_total)}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Grand totals */}
                <div className="asl-card p-4" data-testid="fin-grand">
                    <h3 className="text-lg font-bold asl-navy-text">Grand Totals</h3>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-3 text-xs">
                        <Fact label="Workers" value={grand.worker_count} />
                        <Fact label="Gross" value={fmtUSD(grand.gross_total)} />
                        <Fact label="Net" value={fmtUSD(grand.net_payable_total)} accent />
                        <Fact label="Mgmt fee" value={fmtUSD(grand.management_fee)} />
                        <Fact label="Funding req" value={fmtUSD(grand.funding_required_total)} accent />
                        <Fact label="Funded" value={fmtUSD(grand.funded_amount)} />
                        {/* Iter63 — Grand schedule bonus roll-up. */}
                        {(grand.schedule_bonus_total || 0) > 0 && (
                            <Fact
                                label="Schedule bonuses"
                                value={fmtUSD(grand.schedule_bonus_total)}
                            />
                        )}
                    </div>
                </div>

                {/* Bars */}
                <div className="asl-card p-4">
                    <h3 className="text-sm font-bold asl-navy-text mb-3">Gross by module</h3>
                    <div className="space-y-2" data-testid="fin-bars">
                        {(summary.per_module || []).map((m) => (
                            <div key={m.module_id} className="text-xs">
                                <div className="flex justify-between mb-1"><span>{m.module_code}</span><span className="mono">{fmtUSD(m.gross_total)}</span></div>
                                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                    <div className="h-full asl-navy-bg" style={{ width: `${(m.gross_total / maxBar) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pipeline */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fin-pipeline">
                    {Object.entries(pipeline).map(([k, v]) => (
                        <div key={k} className="asl-card p-3 text-xs">
                            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">{k}</div>
                            <div className="text-lg font-bold asl-navy-text mt-1 mono">{fmtUSD(v)}</div>
                        </div>
                    ))}
                </div>

                <Tabs defaultValue="ledger">
                    <TabsList>
                        <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger ({ledger.length})</TabsTrigger>
                        <TabsTrigger value="fees" data-testid="tab-fees">Management Fees</TabsTrigger>
                    </TabsList>
                    <TabsContent value="ledger">
                        <div className="asl-card overflow-hidden">
                            <div className="flex items-center gap-3 p-3 border-b" style={{ borderColor: "var(--asl-border)" }} data-testid="ledger-toolbar">
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Module</div>
                                <Select value={moduleFilter} onValueChange={setModuleFilter}>
                                    <SelectTrigger className="w-44" data-testid="ledger-module-filter"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">All modules</SelectItem>
                                        {modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <a href={`${API_BASE}/finance/ledger/export.csv?date_from=${from}&date_to=${to}${moduleFilter !== "__all__" ? `&module_id=${moduleFilter}` : ""}`}
                                    target="_blank" rel="noreferrer" className="ml-auto">
                                    <Button size="sm" variant="outline" data-testid="ledger-csv-filtered">
                                        <Download className="h-4 w-4 mr-1" /> CSV
                                    </Button>
                                </a>
                            </div>
                            <table className="asl-table asl-table--zebra" data-testid="ledger-table">
                                <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                    style={{ background: "var(--asl-navy-50)" }}>
                                    <tr>
                                        <th className="text-left px-3 py-2">Date</th>
                                        <th className="text-left px-3 py-2">Module</th>
                                        <th className="text-left px-3 py-2">Type</th>
                                        <th className="text-left px-3 py-2">Subject</th>
                                        <th className="text-right px-3 py-2">Amount</th>
                                        <th className="text-left px-3 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledger.map((r, i) => (
                                        <tr key={i} className="border-t" style={{ borderColor: "var(--asl-border)" }}>
                                            <td className="px-3 py-2 mono text-xs">{r.date}</td>
                                            <td className="px-3 py-2 text-xs">{r.module_code}</td>
                                            <td className="px-3 py-2 text-xs">{r.type}</td>
                                            <td className="px-3 py-2 text-xs">{r.subject}</td>
                                            <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(r.amount)}</td>
                                            <td className="px-3 py-2 text-xs">{r.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>
                    <TabsContent value="fees">
                        <div className="asl-card overflow-hidden">
                            <table className="asl-table asl-table--zebra" data-testid="fees-table">
                                <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                    style={{ background: "var(--asl-navy-50)" }}>
                                    <tr><th className="text-left px-3 py-2">Module</th><th className="text-right px-3 py-2">Periods</th><th className="text-right px-3 py-2">Net Payable</th><th className="text-right px-3 py-2">Mgmt fee</th></tr>
                                </thead>
                                <tbody>
                                    {fees.map((f) => (
                                        <tr key={f.module_id} className="border-t" style={{ borderColor: "var(--asl-border)" }}>
                                            <td className="px-3 py-2 text-xs">{f.module_name}</td>
                                            <td className="px-3 py-2 mono text-xs text-right">{f.period_count}</td>
                                            <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(f.net_payable_total)}</td>
                                            <td className="px-3 py-2 mono text-xs text-right asl-crimson-text font-semibold">{fmtUSD(f.management_fee)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>
                </Tabs>
            </section>
        </AppShell>
    );
}

function Fact({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-sm mono ${accent ? "asl-crimson-text" : "asl-navy-text"} font-semibold`}>{value}</div>
        </div>
    );
}
